import {
  useMutationCreateUser,
  useMutationUpdateUser,
  useQueryGetOneUser,
  Users,
  usersQueryKey,
} from "@apis:user/user";
import { SuperDialog } from "@components:SuperDialog";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { Box, MenuItem } from "@mui/material";
import { useToasterStoreActions } from "@stores:global";
import { omitEmpty } from "@utils:object";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FeatureFlag } from "@:flag";
import { UserRoles } from "@:rbac";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

export const UserModal = ({ isOpen, onClose, userId }: UserModalProps) => {
  const { t } = useTranslation();
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const { data, isLoading } = useQueryGetOneUser(userId!, {
    enabled: !!userId,
  });

  const { register, handleSubmit, formState, control } = useForm({
    defaultValues: {
      name: "",
      enabled: true,
      email: "",
      password: "",
      role: UserRoles.USER,
    },
    values: {
      name: data?.name,
      enabled: data?.enabled,
      email: data?.email,
      password: "",
      role: data?.role,
    },
    // we need to use mode: 'all' for MUI https://github.com/orgs/react-hook-form/discussions/2357#discussioncomment-42073
    mode: "all",
  });

  const mutationCreateUser = useMutationCreateUser({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.users.created"),
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: usersQueryKey.getAll.queryKey,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.users.createFailed"),
        variant: "error",
      });
    },
  });

  const mutationUpdateUser = useMutationUpdateUser({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.users.updated"),
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: usersQueryKey._def,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: t("tenant.users.updateFailed"),
        variant: "error",
      });
    },
  });

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={userId ? t("tenant.users.edit") : t("tenant.users.add")}
      isLoading={isLoading}
      sx={{
        "& .MuiDialog-paper": {
          height: "34rem",
          width: "30rem",
        },
      }}
      actionControls={{
        secondary: {
          label: t("common.cancel"),
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
          },
          label: t("common.save"),
          onClick: async () => {
            await handleSubmit((data) => {
              if (userId) {
                mutationUpdateUser.mutate({
                  id: userId,
                  ...omitEmpty(data),
                });
              } else {
                mutationCreateUser.mutate({
                  name: data.name!,
                  email: data.email!,
                  password: data.password!,
                  role: data.role as Users["POST"]["RequestBody"]["role"],
                  ...(FeatureFlag.UpdateEnabled
                    ? { enabled: data.enabled! }
                    : {}),
                });
              }
            })();
          },
          disabled: !formState.isValid,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <SuperInput.TextField
          required
          label={t("common.name")}
          {...register("name", {
            required: t("common.requiredField", { field: t("common.name") }),
          })}
          errorText={formState.errors.name?.message}
        />
        <SuperInput.TextField
          required
          label={t("common.email")}
          {...register("email", {
            required: t("common.requiredField", { field: t("common.email") }),
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: t("tenant.emailFormatInvalid"),
            },
          })}
          errorText={formState.errors.email?.message}
          type="email"
        />
        <SuperInput.TextField
          required
          label={t("tenant.password")}
          {...register("password", {
            ...(userId
              ? undefined
              : {
                  required: t("common.requiredField", {
                    field: t("tenant.password"),
                  }),
                }),
          })}
          errorText={formState.errors.password?.message}
          type="password"
        />

        <Box
          sx={{
            display: "flex",
            gap: "3rem",
            alignItems: "center",
          }}
        >
          <Controller
            name="role"
            defaultValue={UserRoles.USER}
            rules={{
              required: t("common.requiredField", { field: t("common.role") }),
            }}
            control={control}
            render={({ field }) => (
              <SuperInput.Select
                required
                sx={{
                  flex: 1,
                }}
                {...field}
                width="100%"
                label={t("common.role")}
                errorText={formState.errors.role?.message}
              >
                <MenuItem value={UserRoles.USER}>
                  {t(`tenant.role.${UserRoles.USER}`)}
                </MenuItem>
                <MenuItem value={UserRoles.ADMINISTRATOR}>
                  {t(`tenant.role.${UserRoles.ADMINISTRATOR}`)}
                </MenuItem>
              </SuperInput.Select>
            )}
          />
          {FeatureFlag.UpdateEnabled && (
            <Controller
              name="enabled"
              defaultValue={true}
              control={control}
              render={({ field }) => (
                <SuperInput.Switch
                  required
                  sx={{
                    marginTop: "1rem",
                  }}
                  label={t("common.enabled")}
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Box>
      </Box>
    </SuperDialog>
  );
};
